import { Link } from "react-router-dom";
import { Twitch, Youtube, Instagram, HandCoins } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { APPS } from "@/data/apps";

const wavePath = (base, amp, phase) => {
  let d = "";
  for (let x = 0; x <= 1440; x += 30) {
    const y = base + amp * Math.sin((x / 1440) * Math.PI * 2 + phase);
    d += `${x === 0 ? "M" : "L"}${x},${y.toFixed(1)} `;
  }
  return d;
};

const WAVE_PATHS = [
  { base: 150, amp: 45, phase: 0 },
  { base: 185, amp: 55, phase: 1.2 },
  { base: 220, amp: 40, phase: 2.4 },
  { base: 255, amp: 50, phase: 3.6 },
];

export const Waves = () => {
  const wave = (
    <svg viewBox="0 0 1440 320" preserveAspectRatio="none" className="h-full w-1/2 shrink-0" fill="none">
      {WAVE_PATHS.map((p, i) => (
        <path key={i} d={wavePath(p.base, p.amp, p.phase)} stroke="#9146FF" strokeOpacity={0.5 - i * 0.08} strokeWidth="1.2" />
      ))}
    </svg>
  );
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-56 overflow-hidden [mask-image:linear-gradient(to_top,black_30%,transparent)]" data-testid="footer-waves">
      <div className="animate-wave flex h-full w-[200%]">
        {wave}
        {wave}
      </div>
    </div>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="relative overflow-hidden border-t border-white/10 px-6 pb-24 pt-16 md:px-12" data-testid="site-footer">
      <Waves />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <img src="/assets/fc-logo.webp" alt="FC — Fabio Comelli" className="h-12 w-12" data-testid="footer-logo" />
          <p className="mt-4 max-w-xs font-mono text-xs leading-relaxed text-muted2">{t.footer.rights}</p>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted2">{t.footer.apps}</p>
          <ul className="mt-5 space-y-3 text-sm">
            {APPS.map((a) => (
              <li key={a.slug}>
                <Link to={`/apps/${a.slug}`} data-testid={`footer-app-${a.slug}`} className="transition-colors duration-300 hover:text-neon">
                  {a.name}
                </Link>
              </li>
            ))}
            <li>
              <a href="https://www.hydeofficialstore.com/prodotto.php?id=31" target="_blank" rel="noopener noreferrer" data-testid="footer-magic-link" className="text-muted2 transition-colors duration-300 hover:text-neon">
                Parole in Treno ↗
              </a>
            </li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted2">{t.footer.legal}</p>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <Link to="/support" data-testid="footer-support-link" className="transition-colors duration-300 hover:text-neon">
                {t.support.kicker}
              </Link>
            </li>
            <li>
              <Link to="/privacy" data-testid="footer-privacy-index" className="transition-colors duration-300 hover:text-neon">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted2">{t.footer.contact}</p>
          <a href="mailto:info@fabiocomelli.it" data-testid="footer-email-link" className="mt-5 block text-sm transition-colors duration-300 hover:text-neon">
            info@fabiocomelli.it
          </a>
          <a href="https://www.twitch.tv/DottorFabio" target="_blank" rel="noopener noreferrer" data-testid="footer-twitch-link" className="mt-3 flex items-center gap-2 text-sm text-muted2 transition-colors duration-300 hover:text-neon">
            <Twitch className="h-4 w-4" />
            DottorFabio
          </a>
          <a href="https://www.youtube.com/@fabiocomelli" target="_blank" rel="noopener noreferrer" data-testid="footer-youtube-link" className="mt-3 flex items-center gap-2 text-sm text-muted2 transition-colors duration-300 hover:text-neon">
            <Youtube className="h-4 w-4" />
            @fabiocomelli
          </a>
          <a href="https://www.instagram.com/fabio_comelli/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="mt-3 flex items-center gap-2 text-sm text-muted2 transition-colors duration-300 hover:text-neon">
            <Instagram className="h-4 w-4" />
            @fabio_comelli
          </a>
          <a
            href="https://paypal.me/fabiocomelli"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-paypal-donate"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#009CDE]/40 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-[#4fc3f7] transition-colors duration-300 hover:border-[#009CDE] hover:bg-[#009CDE]/10"
          >
            <HandCoins className="h-4 w-4" />
            {t.footer.donate}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
